import { useCallback, useEffect, useMemo, useState } from 'react';
import { History } from '@mui/icons-material';
import { Badge, Step, StepButton, Stepper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { DailyNavSetupCompany, FirmDailyNavConfigVersion } from 'api';
import { firmDailyNavSettingsUrl, firmFeatureSettingsUrl } from 'common/config/urls';
import { selectDailyNavGoverningVersions } from 'common/constants/dailyNav';
import { DAILY_NAV_SETUP_PAGE_TITLE, DAILY_NAV_SETUP_TAB, FIRM_SETTINGS } from 'common/constants/firms';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import { GridSkeleton } from 'components/Grid';
import { Alert, Box, Button, Stack, Text, TextLink, Tooltip } from 'components/ui';
import { useSecondaryNavigationRegistration, useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import {
  buildDailyNavTertiaryNavigationItems,
  buildFirmSettingsNavigationItems,
} from 'pages/Firms/firmSettingsNavigation';
import {
  useGetDailyNavConfigApplyPreview,
  useGetDailyNavSetupCompanies,
  useGetFirmDailyNavCompanyConfigs,
  useGetFirmDailyNavConfig,
  useUpdateFirmDailyNavConfig,
} from 'services/hooks/dailyNav';
import { useUploadDailyNavReportTemplate } from 'services/hooks/firms/useDailyNavReport';
import { PrismThemeProvider } from 'theme';
import CompanyListStep from './components/CompanyListStep';
import CompanySetupStep from './components/CompanySetupStep';
import DailyNavSetupContextPanel, { DailyNavSetupPanelContext } from './components/DailyNavSetupContextPanel';
import FirmDeliveryStep from './components/FirmDeliveryStep';
import FirmMarketStep from './components/FirmMarketStep';
import FirmSaveReviewDialog, { buildFirmSaveReviewGroups } from './components/FirmSaveReviewDialog';
import FirmThresholdsStep from './components/FirmThresholdsStep';
import LeaveWithUnsavedChangesDialog from './components/LeaveWithUnsavedChangesDialog';
import ReviewStep from './components/ReviewStep';
import { DAILY_NAV_SETUP_TEST_IDS } from './DailyNavSetup.testIds';
import { COMPANY_STEPS, DAILY_NAV_SETUP_STEPS, DailyNavSetupStep, SetupDataState } from './dailyNavSetup.utils';
import { useFirmSetupForm } from './hooks/useFirmSetupForm';
import { parseBlockedLocation, useLeaveGuard } from './hooks/useLeaveGuard';
import { useSetupSessionChanges } from './hooks/useSetupSessionChanges';

const STEP_PARAM = 'step';
const COMPANY_PARAM = 'company';
const WALK_PARAM = 'walk';

const isSetupStep = (value: string | null): value is DailyNavSetupStep =>
  DAILY_NAV_SETUP_STEPS.some(step => step.key === value);

// Moving between firm steps keeps the draft, so only leaving them is guarded.
const isFirmStepMove = (location: Parameters<typeof parseBlockedLocation>[0]) => {
  const { pathname, search } = parseBlockedLocation(location);
  const step = search.get(STEP_PARAM);
  return pathname === window.location.pathname && isSetupStep(step) && !COMPANY_STEPS.has(step);
};

const DailyNavSetupContent = () => {
  const [{ firmInfo, user }] = useStore() as unknown as UseStoreValues;
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  const firmId = firmInfo?.id ?? null;
  const firmSlug = firmInfo?.slug ?? '';
  const { canMutateFirmScopedEntities: canEdit } = useFirmScopedPermissions({ firmId });

  const { data: firmConfig, isLoading: isFirmConfigLoading } = useGetFirmDailyNavConfig(firmId);
  const hasSavedFirmConfig = Boolean(firmConfig?.id);
  const { data: companyConfigs, isLoading: isCompaniesLoading } = useGetFirmDailyNavCompanyConfigs(firmId);
  const {
    data: setupData,
    isLoading: isSetupLoading,
    isError: isSetupError,
    errorMessage: setupErrorMessage,
  } = useGetDailyNavSetupCompanies(firmId, hasSavedFirmConfig);
  let setupState: SetupDataState = 'ready';
  if (isSetupError) {
    setupState = 'error';
  } else if (isSetupLoading || !setupData) {
    setupState = 'loading';
  }
  const { updateFirmDailyNavConfig, isPending: isSavingFirm } = useUpdateFirmDailyNavConfig();
  const { mutateAsync: uploadReportTemplate, isPending: isUploadingTemplate } = useUploadDailyNavReportTemplate();

  const form = useFirmSetupForm(firmConfig);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isFirmSaveDialogOpen, setIsFirmSaveDialogOpen] = useState(false);
  const { changes, recordFirmSave, recordCompanySave } = useSetupSessionChanges(firmId);

  // Edit mode: the firm already runs Daily NAV, so history and the open NAV day matter.
  const { data: firmApplyPreview } = useGetDailyNavConfigApplyPreview(hasSavedFirmConfig ? firmId : null);
  const { hasDivergence: hasFirmDivergence, nextNavDate } = useMemo(
    () => selectDailyNavGoverningVersions(firmApplyPreview),
    [firmApplyPreview]
  );

  const companies = useMemo(() => companyConfigs?.companies ?? [], [companyConfigs]);
  const setupByCompany = useMemo(
    () => new Map<number, DailyNavSetupCompany>((setupData?.companies ?? []).map(row => [row.company_id, row])),
    [setupData]
  );

  const stepParam = searchParams.get(STEP_PARAM);
  const companyParam = Number(searchParams.get(COMPANY_PARAM)) || null;
  const isWalkthrough = searchParams.get(WALK_PARAM) === '1';
  const areCompanyStepsLocked = !hasSavedFirmConfig || form.isDirty;

  const goTo = useCallback(
    (step: DailyNavSetupStep, companyId: number | null = null, walk = false) => {
      const params = new URLSearchParams({ [STEP_PARAM]: step });
      if (companyId !== null) {
        params.set(COMPANY_PARAM, String(companyId));
      }
      if (walk) {
        params.set(WALK_PARAM, '1');
      }
      setSearchParams(params);
    },
    [setSearchParams]
  );

  // Resume where setup left off when no step is in the URL.
  const isLoading = isFirmConfigLoading || isCompaniesLoading;
  useEffect(() => {
    if (isLoading || isSetupStep(stepParam)) {
      return;
    }
    setSearchParams(
      { [STEP_PARAM]: hasSavedFirmConfig ? DailyNavSetupStep.COMPANIES : DailyNavSetupStep.MARKET },
      { replace: true }
    );
  }, [isLoading, stepParam, hasSavedFirmConfig, setSearchParams]);

  const requestedStep = isSetupStep(stepParam) ? stepParam : DailyNavSetupStep.MARKET;
  // A deep link past the firm steps is sent back until the firm config is saved.
  const activeStep = COMPANY_STEPS.has(requestedStep) && !hasSavedFirmConfig ? DailyNavSetupStep.MARKET : requestedStep;
  const activeIndex = DAILY_NAV_SETUP_STEPS.findIndex(step => step.key === activeStep);
  const selectedCompany =
    activeStep === DailyNavSetupStep.COMPANIES && companyParam
      ? (companies.find(row => row.company_id === companyParam) ?? null)
      : null;

  const firmLeaveGuard = useLeaveGuard(!COMPANY_STEPS.has(activeStep) && form.isDirty, isFirmStepMove);

  const secondaryNavigation = useMemo(
    () => ({ title: FIRM_SETTINGS, items: buildFirmSettingsNavigationItems(firmSlug, { dailyNavEnabled: true }) }),
    [firmSlug]
  );
  useSecondaryNavigationRegistration(secondaryNavigation);

  // Settings / Companies / Setup wizard: the Daily NAV pages share one tertiary tab strip.
  const tertiaryNavigation = useMemo(
    () => ({ ariaLabel: 'Daily NAV', items: buildDailyNavTertiaryNavigationItems(firmSlug), value: DAILY_NAV_SETUP_TAB }),
    [firmSlug]
  );
  useTertiaryNavigationRegistration(tertiaryNavigation);

  const handleTemplateFileChange = useCallback(
    async (file: File | null) => {
      setTemplateFile(file);
      if (!file || !firmId) {
        return;
      }
      try {
        form.applyUploadedTemplate(await uploadReportTemplate({ firmId, file }));
      } catch {
        setTemplateFile(null);
      }
    },
    [firmId, form, uploadReportTemplate]
  );

  // The step header lets a user jump ahead, so the save checks every firm step, not just this one.
  const incompleteFirmStep = DAILY_NAV_SETUP_STEPS.find(
    step => !COMPANY_STEPS.has(step.key) && form.stepValidity[step.key] === false
  );

  const saveFirm = async () => {
    if (!firmId || incompleteFirmStep) {
      return;
    }
    const before = hasSavedFirmConfig ? (firmConfig ?? null) : null;
    form.acceptNextConfig();
    const saved = await updateFirmDailyNavConfig(firmId, form.toPayload());
    setIsFirmSaveDialogOpen(false);
    if (!saved) {
      form.acceptNextConfig(false);
      return;
    }
    recordFirmSave(before, saved);
    enqueueSnackbar('Firm Daily NAV settings saved', { variant: 'success' });
    firmLeaveGuard.allowNextNavigation();
    goTo(DailyNavSetupStep.COMPANIES);
  };

  // A first save goes straight through; an edit shows what it changes first.
  const saveFirmAndContinue = () => {
    // An unsaved firm always saves, even when the seeded defaults were kept as they are.
    if (!form.isDirty && hasSavedFirmConfig) {
      goTo(DailyNavSetupStep.COMPANIES);
      return;
    }
    if (hasSavedFirmConfig) {
      setIsFirmSaveDialogOpen(true);
      return;
    }
    saveFirm();
  };

  // The draft as a version, so the shared diff can compare it with what is saved.
  const firmSaveGroups = useMemo(() => {
    if (!isFirmSaveDialogOpen || !firmConfig) {
      return [];
    }
    const draftVersion = {
      ...firmConfig,
      ...form.toPayload(),
      report_template_filename: form.draft.reportTemplateFilename,
    } as unknown as FirmDailyNavConfigVersion;
    return buildFirmSaveReviewGroups(firmConfig, draftVersion);
    // toPayload reads form.draft, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirmSaveDialogOpen, firmConfig, form.draft]);

  // Wait for the user, whose roles decide access, rather than flash the no-access state.
  if (isLoading || !firmId || !user) {
    return <GridSkeleton />;
  }

  // Setup writes firm and company config, so it is for firm editors only.
  if (!canEdit) {
    return (
      <Box sx={{ pt: 1, maxWidth: 800 }}>
        <Text variant="h6" gutterBottom>
          {DAILY_NAV_SETUP_PAGE_TITLE}
        </Text>
        <Alert severity="info" data-testid={DAILY_NAV_SETUP_TEST_IDS.noAccessAlert}>
          Daily NAV setup is available to firm editors only.{' '}
          <TextLink<typeof RouterLink> component={RouterLink} to={firmDailyNavSettingsUrl(firmSlug)}>
            View Daily NAV Settings
          </TextLink>
          .
        </Alert>
      </Box>
    );
  }

  const isFirmStep = !COMPANY_STEPS.has(activeStep);
  const isLastFirmStep = activeStep === DailyNavSetupStep.DELIVERY;
  const isStepValid = form.stepValidity[activeStep] ?? true;
  const disabled = !canEdit;

  let panelContext: DailyNavSetupPanelContext = { kind: 'firm' };
  if (selectedCompany) {
    panelContext = { kind: 'company', company: selectedCompany };
  } else if (activeStep === DailyNavSetupStep.COMPANIES) {
    panelContext = { kind: 'companies', companies };
  }

  const renderStep = () => {
    switch (activeStep) {
      case DailyNavSetupStep.MARKET:
        return <FirmMarketStep form={form} disabled={disabled} />;
      case DailyNavSetupStep.THRESHOLDS:
        return <FirmThresholdsStep form={form} disabled={disabled} />;
      case DailyNavSetupStep.DELIVERY:
        return (
          <FirmDeliveryStep
            form={form}
            disabled={disabled}
            templateFile={templateFile}
            isUploadingTemplate={isUploadingTemplate}
            onTemplateFileChange={handleTemplateFileChange}
          />
        );
      case DailyNavSetupStep.COMPANIES:
        return selectedCompany ? (
          <CompanySetupStep
            // Remount per company so no draft carries over from the last one.
            key={selectedCompany.company_id}
            firmId={firmId}
            company={selectedCompany}
            setup={setupByCompany.get(selectedCompany.company_id)}
            setupState={setupState}
            setupErrorMessage={setupErrorMessage}
            disabled={disabled}
            onBack={() => goTo(DailyNavSetupStep.COMPANIES)}
            onSaved={() => goTo(DailyNavSetupStep.COMPANIES)}
            onConfigSaved={(before, after) =>
              recordCompanySave(selectedCompany.company_id, selectedCompany.company_name ?? '', before, after)
            }
            walkthrough={
              isWalkthrough
                ? {
                  companies,
                  onNavigate: companyId => goTo(DailyNavSetupStep.COMPANIES, companyId, true),
                  onFinish: () => goTo(DailyNavSetupStep.REVIEW),
                }
                : undefined
            }
          />
        ) : (
          <CompanyListStep
            companies={companies}
            setupByCompany={setupByCompany}
            setupState={setupState}
            setupErrorMessage={setupErrorMessage}
            onSelectCompany={companyId => goTo(DailyNavSetupStep.COMPANIES, companyId)}
            onStartWalkthrough={companyId => goTo(DailyNavSetupStep.COMPANIES, companyId, true)}
          />
        );
      case DailyNavSetupStep.REVIEW:
        return firmConfig ? (
          <ReviewStep
            firmId={firmId}
            firmSlug={firmSlug}
            firmConfig={firmConfig}
            companies={companies}
            canEdit={canEdit}
            isDailyNavEnabled={Boolean(firmInfo?.daily_nav_enabled)}
            changes={changes}
            onSelectCompany={companyId => goTo(DailyNavSetupStep.COMPANIES, companyId)}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ pt: 1, pb: 10, maxWidth: 1100 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Text variant="h6" gutterBottom>
          {DAILY_NAV_SETUP_PAGE_TITLE}
        </Text>
        {hasSavedFirmConfig && (
          <Badge color="warning" variant="dot" invisible={!hasFirmDivergence}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<History />}
              onClick={() => setIsPanelOpen(true)}
              data-testid={DAILY_NAV_SETUP_TEST_IDS.contextPanelButton}>
              History &amp; status
            </Button>
          </Badge>
        )}
      </Stack>
      <Text variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Firm settings save when you finish Reports &amp; delivery, and each company saves on its own, so you can
        leave and come back at any point.
      </Text>
      {!firmInfo?.daily_nav_enabled && (
        <Alert severity="warning" sx={{ mb: 3 }} data-testid={DAILY_NAV_SETUP_TEST_IDS.notEnabledAlert}>
          Daily NAV is not enabled for this firm, so nothing will generate until it is.{' '}
          <TextLink<typeof RouterLink> component={RouterLink} to={firmFeatureSettingsUrl(firmSlug)}>
            Enable it in Settings
          </TextLink>
          .
        </Alert>
      )}
      <Stepper nonLinear activeStep={activeIndex} sx={{ mb: 4 }} data-testid={DAILY_NAV_SETUP_TEST_IDS.stepper}>
        {DAILY_NAV_SETUP_STEPS.map(step => {
          const isLocked = COMPANY_STEPS.has(step.key) && areCompanyStepsLocked;
          return (
            <Step key={step.key} disabled={isLocked}>
              <Tooltip
                title={isLocked ? 'Save the firm settings first, on Reports & delivery.' : ''}
                describeChild>
                <span>
                  <StepButton
                    onClick={() => goTo(step.key)}
                    disabled={isLocked}
                    data-testid={DAILY_NAV_SETUP_TEST_IDS.step(step.key)}>
                    {step.label}
                  </StepButton>
                </span>
              </Tooltip>
            </Step>
          );
        })}
      </Stepper>
      {renderStep()}
      {!selectedCompany && (
        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 4 }}>
          <Button
            disabled={activeIndex === 0}
            onClick={() => goTo(DAILY_NAV_SETUP_STEPS[activeIndex - 1].key)}
            data-testid={DAILY_NAV_SETUP_TEST_IDS.backButton}>
            Back
          </Button>
          {isLastFirmStep && incompleteFirmStep && incompleteFirmStep.key !== activeStep && (
            <Alert
              severity="warning"
              sx={{ flex: 1, py: 0 }}
              data-testid={DAILY_NAV_SETUP_TEST_IDS.incompleteStepAlert}
              action={
                <Button
                  size="small"
                  onClick={() => goTo(incompleteFirmStep.key)}
                  data-testid={DAILY_NAV_SETUP_TEST_IDS.incompleteStepButton}>
                  Go to {incompleteFirmStep.label}
                </Button>
              }>
              {incompleteFirmStep.label} isn&apos;t complete yet, so the firm settings can&apos;t be saved.
            </Alert>
          )}
          {activeStep !== DailyNavSetupStep.REVIEW && (
            <Button
              variant="contained"
              disabled={
                isFirmStep &&
                (!isStepValid ||
                  (isLastFirmStep &&
                    (disabled || isSavingFirm || isUploadingTemplate || Boolean(incompleteFirmStep))))
              }
              onClick={() =>
                isLastFirmStep ? saveFirmAndContinue() : goTo(DAILY_NAV_SETUP_STEPS[activeIndex + 1].key)
              }
              loading={isLastFirmStep && isSavingFirm}
              data-testid={DAILY_NAV_SETUP_TEST_IDS.nextButton}>
              {isLastFirmStep && (form.isDirty || !hasSavedFirmConfig) ? 'Save firm settings & continue' : 'Next'}
            </Button>
          )}
        </Stack>
      )}
      {hasSavedFirmConfig && (
        <DailyNavSetupContextPanel
          open={isPanelOpen}
          onClose={() => setIsPanelOpen(false)}
          firmId={firmId}
          context={panelContext}
        />
      )}
      <LeaveWithUnsavedChangesDialog
        open={firmLeaveGuard.isLeavePending}
        body="Your firm setting changes haven't been saved. They save at the end of Reports & delivery; leaving now discards them."
        onKeepEditing={firmLeaveGuard.cancelLeave}
        onDiscard={() => {
          form.reset();
          firmLeaveGuard.confirmLeave();
        }}
      />
      <FirmSaveReviewDialog
        open={isFirmSaveDialogOpen}
        groups={firmSaveGroups}
        nextNavDate={nextNavDate}
        isSaving={isSavingFirm}
        onCancel={() => setIsFirmSaveDialogOpen(false)}
        onConfirm={saveFirm}
        onEditStep={step => {
          setIsFirmSaveDialogOpen(false);
          goTo(step);
        }}
      />
    </Box>
  );
};

// components/ui reads the Prism palette, which the app does not provide globally.
const DailyNavSetup = () => (
  <PrismThemeProvider>
    <DailyNavSetupContent />
  </PrismThemeProvider>
);

export default DailyNavSetup;
